import React, { useEffect, useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  Squares2X2Icon,
  UsersIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  DocumentTextIcon,
  ClockIcon,
  // ChartBarIcon,
  FlagIcon,
  CurrencyRupeeIcon,
  CogIcon,
  BellIcon,
  TicketIcon,
  BanknotesIcon,
  ShoppingBagIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { RootState } from '../store/store'
import { logout } from '../store/slices/authSlice'
import logoIcon from '../assets/logo-icon.png'

const nav = [
  { name: 'Overview',        href: '/admin',              icon: Squares2X2Icon },
  { name: 'Client Records',  href: '/admin/clients',      icon: UsersIcon },
  { name: 'KYC Management',  href: '/admin/kyc',          icon: ShieldCheckIcon },
  { name: 'Recommendations', href: '/admin/content',      icon: LightBulbIcon },
  { name: 'Blog Management', href: '/admin/blogs',        icon: DocumentTextIcon },
  { name: 'Plans',           href: '/admin/plans',        icon: CurrencyRupeeIcon },
  { name: 'Coupons',         href: '/admin/coupons',      icon: TicketIcon },
  { name: 'Transactions',    href: '/admin/transactions', icon: BanknotesIcon },
  { name: 'Purchases',       href: '/admin/purchases',    icon: ShoppingBagIcon },
  { name: 'Documents',       href: '/admin/documents',    icon: DocumentTextIcon },
  { name: 'Audit Logs',     href: '/admin/audit-trail',  icon: ClockIcon },
  // { name: 'Performance',     href: '/admin/subscriptions',icon: ChartBarIcon },
  { name: 'Grievances',      href: '/admin/complaints',   icon: FlagIcon },
  { name: 'Notifications',   href: '/admin/notifications',icon: BellIcon, comingSoon: true },
  { name: 'Settings',        href: '/admin/settings',     icon: CogIcon },
]

const Sidebar: React.FC<{ onClose?: () => void; collapsed?: boolean; onToggle?: () => void }> = ({ onClose, collapsed = false, onToggle }) => {
  const location = useLocation()
  const dispatch = useDispatch()
  const { user } = useSelector((s: RootState) => s.auth)
  const [comingSoon, setComingSoon] = useState<string | null>(null)

  const isActive = (href: string) =>
    href === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(href)

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-950 text-white transition-[width] duration-250 ease-in-out" style={{ width: collapsed ? 76 : 256 }}>
      {/* Brand */}
      <div className={`border-b border-gray-800 ${collapsed ? 'px-3 pt-4 pb-3' : 'px-5 pt-6 pb-4'}`}>
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3 mb-4'}`}>
          <img src={logoIcon} alt="AnalyzerView" className="w-9 h-9 object-contain" />
          <div className={`min-w-0 transition-all duration-200 ${collapsed ? 'w-0 overflow-hidden opacity-0' : 'opacity-100'}`}>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-medium" style={{ fontSize: 9 }}>Research Analyst</p>
            <p className="text-sm font-semibold text-white leading-tight">Compliance Desk</p>
          </div>
        </div>
        {onToggle && (
          <button onClick={onToggle} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} className={`mt-3 flex h-8 w-full items-center rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white ${collapsed ? 'justify-center' : 'justify-end pr-2'}`}>
            {collapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
          </button>
        )}
        {/* SEBI badge */}
        <div className={`bg-gray-900 rounded-lg px-3 py-2.5 border border-gray-800 transition-all duration-200 ${collapsed ? 'mt-3 hidden' : ''}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-gray-500 font-medium" style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em' }}>SEBI Registration</span>
            <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
          </div>
          <p className="text-xs font-mono text-gray-300 font-medium">INH000023719</p>
          <p className="text-gray-500 font-mono mt-0.5" style={{ fontSize: 10 }}>BSE RAASB Enlistment No. 7041</p>
          <p className="text-gray-500 mt-1 leading-tight" style={{ fontSize: 10 }}>
            Advisory only. No execution. No broker credentials. No proxy trading.
          </p>
        </div>
      </div>

      {/* Nav */}
      <nav className={`flex-1 py-4 space-y-0.5 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'}`}>
        {nav.map(item => {
          const active = isActive(item.href)
          const Icon = item.icon
          if (item.comingSoon) {
            return (
              <button
                key={item.name}
                onClick={() => setComingSoon(item.name)}
                title={collapsed ? item.name : undefined}
                className={`w-full flex items-center rounded-lg text-sm font-medium transition-colors text-gray-500 hover:bg-gray-800 hover:text-gray-300 ${collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2'}`}
              >
                <Icon style={{ width: 17, height: 17, flexShrink: 0 }} />
                <span className={`flex-1 text-left whitespace-nowrap transition-all duration-200 ${collapsed ? 'w-0 overflow-hidden opacity-0' : 'opacity-100'}`}>{item.name}</span>
                {!collapsed && <span className="text-amber-400/70 bg-amber-500/10 rounded px-1.5 py-0.5 font-medium" style={{ fontSize: 9 }}>SOON</span>}
              </button>
            )
          }
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              title={collapsed ? item.name : undefined}
              className={`flex items-center rounded-lg text-sm font-medium transition-colors ${collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2'} ${
                active
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-gray-100'
              }`}
            >
              <Icon style={{ width: 17, height: 17, flexShrink: 0 }} className={active ? 'text-amber-400' : ''} />
              <span className={`whitespace-nowrap transition-all duration-200 ${collapsed ? 'w-0 overflow-hidden opacity-0' : 'opacity-100'}`}>{item.name}</span>
            </Link>
          )
        })}
      </nav>

      {/* Coming soon popup */}
      {comingSoon && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4"
          onClick={() => setComingSoon(null)}
        >
          <div
            className="bg-gray-900 border border-gray-800 rounded-xl max-w-sm w-full p-6 text-center shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-500/15 flex items-center justify-center">
              <ClockIcon style={{ width: 24, height: 24 }} className="text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-1">{comingSoon}</h3>
            <p className="text-sm text-amber-400 font-medium mb-3">Coming Soon</p>
            <p className="text-sm text-gray-400 mb-5">
              This feature is under development and will be available in an upcoming release.
            </p>
            <button
              onClick={() => setComingSoon(null)}
              className="w-full bg-amber-500 hover:bg-amber-400 text-gray-950 font-semibold text-sm rounded-lg px-4 py-2.5 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* User row */}
      <div className={`border-t border-gray-800 py-3 flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-4'}`}>
        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-semibold text-sm flex-shrink-0">
          {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
        </div>
        <div className={`flex-1 min-w-0 transition-all duration-200 ${collapsed ? 'w-0 overflow-hidden opacity-0' : ''}`}>
          <p className="text-xs font-medium text-gray-200 truncate">{user?.full_name || 'Research Advisor'}</p>
          <p className="text-gray-500 truncate" style={{ fontSize: 10 }}>{user?.email || ''}</p>
        </div>
        <button
          onClick={() => dispatch(logout() as any)}
          className="text-gray-500 hover:text-gray-200 transition-colors"
          title="Logout"
        >
          <ArrowRightOnRectangleIcon style={{ width: 16, height: 16 }} />
        </button>
      </div>
    </div>
  )
}

const AdminLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('admin-sidebar-collapsed') === 'true')

  useEffect(() => {
    localStorage.setItem('admin-sidebar-collapsed', String(sidebarCollapsed))
  }, [sidebarCollapsed])

  return (
    <div className="min-h-screen flex bg-dark-950" style={{ '--admin-sidebar-width': `${sidebarCollapsed ? 76 : 256}px` } as React.CSSProperties}>
      {/* Desktop sidebar */}
      <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 z-30" style={{ width: sidebarCollapsed ? 76 : 256 }}>
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(value => !value)} />
      </div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative z-50 h-full">
            <Sidebar onClose={() => setMobileOpen(false)} />
          </div>
          <button
            className="absolute top-4 right-4 text-white z-50"
            onClick={() => setMobileOpen(false)}
          >
            <XMarkIcon style={{ width: 24, height: 24 }} />
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-col flex-1 min-h-screen lg:ml-[var(--admin-sidebar-width)] transition-[margin] duration-250 ease-in-out">
        {/* Mobile topbar */}
        <div className="lg:hidden sticky top-0 z-20 bg-dark-900 border-b border-dark-700 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setMobileOpen(true)} className="text-gray-400">
            <Bars3Icon style={{ width: 24, height: 24 }} />
          </button>
          <span className="font-semibold text-white text-sm">Compliance Desk</span>
        </div>

        <main className="flex-1 w-full max-w-[1600px] mx-auto px-4 py-5 sm:px-6 lg:px-8 lg:py-7">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
